import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { CourseSidebar } from './components/CourseSidebar';
import { LessonContent } from './components/LessonContent';
import { WelcomeScreenComponent } from './components/welcomeScreen';
import { useStudent } from '../context/StudentContext';
import { sortObjects } from 'src/components/helpers';
import { getCourseProgress } from '../utils';
import { screenSizes } from 'src/assets/styles/Literals';

const CourseViewContainer = styled.div`
  display: flex;
  min-height: calc(100vh - 4rem);
  max-width: 1400px;
  margin: 0 auto;
  background-color: #ffffff;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);

  @media (max-width: ${screenSizes.tablet}) {
    flex-direction: column;
    box-shadow: none;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 2rem;
  background-color: #ffffff;
  position: relative;
  overflow-y: auto;

  @media (max-width: ${screenSizes.tablet}) {
    padding: 1rem;
    min-height: calc(100vh - 4rem);
  }

  @media (max-width: ${screenSizes.mobile}) {
    padding: 0.5rem;
  }
`;

const CourseView = (props) => {
  const { course } = props;
  const [currentLesson, setCurrentLesson] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { updateLessonProgress } = useStudent();
  const { student } = useStudent();
  const courseProgress = getCourseProgress(student, course.id);

  // Close sidebar on mobile when lesson is selected
  useEffect(() => {
    if (currentLesson && window.innerWidth <= screenSizes.tablet) {
      setSidebarOpen(false);
    }
  }, [currentLesson]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > screenSizes.tablet) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const groupedLessons = sortObjects(course?.AcademyLessons, 'order')?.reduce((acc, lesson) => {
    const category = lesson.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(lesson);
    return acc;
  }, {});

  const handleLessonSelect = (lesson) => {
    if (!lesson.locked) {
      setCurrentLesson(lesson);
    }
  };

  const handleLessonComplete = () => {
    if (currentLesson) {
      updateLessonProgress(course.id, currentLesson.id, true);
    }
  };

  const getCourseStats = () => {
    const totalLessons = course?.AcademyLessons.length || 0;
    const completedLessons = courseProgress?.completedLessons.length || 0;
    const categories = [...new Set(course?.AcademyLessons.map((lesson) => lesson.category))];
    const totalCategories = categories.length;

    return {
      totalLessons,
      completedLessons,
      totalCategories,
    };
  };

  const progress = Math.round(
    ((courseProgress?.completedLessons?.length || 0) / course?.AcademyLessons?.length) * 100
  );

  const stats = getCourseStats();

  return (
    <CourseViewContainer>
      <CourseSidebar
        course={course}
        currentLesson={currentLesson}
        onLessonSelect={handleLessonSelect}
        progress={progress}
        groupedLessons={groupedLessons}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />
      <ContentArea>
        {currentLesson ? (
          <LessonContent
            lesson={currentLesson}
            onComplete={handleLessonComplete}
            course={course}
            onLessonSelect={handleLessonSelect}
            guest={student}
          />
        ) : (
          <WelcomeScreenComponent
            course={course}
            courseProgress={progress}
            stats={stats}
            setCurrentLesson={setCurrentLesson}
          />
        )}
      </ContentArea>
    </CourseViewContainer>
  );
};

export { CourseView };
